// frontend/src/admin/v2/DocsIndex.jsx
import { useMemo } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
  Alert, Box, Skeleton, Stack, Typography,
} from '@mui/material'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { useDocs, DOCS_403 } from './hooks/useDocs'
import { SP, FS, hoverOnly } from '../../theme/tokens'
import Panel from '../../components/common/Panel'
import Pill from '../../components/common/Pill'
import ListToolbar from '../../components/common/ListToolbar'
import EmptyState from '../../components/common/EmptyState'
import useListQuery from '../../components/common/listQuery/useListQuery'

/**
 * DocsIndex — /admin/docs page.
 *
 * @description
 *   Searchable index of in-app runbooks. Grouped by category
 *   (operations | architecture). Filters client-side on title + summary +
 *   topic, with the list anatomy of `tabellen-filter-suche.md` §2.1: the search
 *   and the result counter in Z1, the active-filter chip in Z3, the cards in
 *   the data panel.
 *
 *   🔴 **The search is the FIRST control of the page** (§13.3 E5): the
 *   component catalog used to sit above it, so the search stood „under the
 *   card". It now sits BELOW the data panel — it is the entrance to a rare
 *   tool surface, not the page's main content.
 *
 *   🔴 **There is no filter.** `TABELLEN-INVENTAR.md:161` named a „Missing in
 *   build" switch; measured, it does not exist — the chip on the card is
 *   conditional on `!doc.exists` (§1.4 of the plan). This view does not invent
 *   one.
 */
const CATEGORY_LABEL = {
  operations:        'Operations',
  architecture:      'Architecture',
  'purchase-orders': 'Purchase orders',
}

export default function DocsIndex() {
  const { data, loading, error, refetch } = useDocs()
  const query = useListQuery({
    dimensions: [{ key: 'q', kind: 'single', label: 'Search' }],
  })
  const { state, set, clearAll, chips } = query
  const q = state.q || ''

  const gefiltert = useMemo(() => {
    if (!data?.docs) return {}
    const needle = q.trim().toLowerCase()
    const matches = !needle
      ? data.docs
      : data.docs.filter(d =>
          d.title.toLowerCase().includes(needle)
          || d.summary.toLowerCase().includes(needle)
          || d.topic.toLowerCase().includes(needle)
        )
    const grouped = {}
    for (const d of matches) {
      (grouped[d.category] = grouped[d.category] || []).push(d)
    }
    return grouped
  }, [data, q])

  const totalMatches = Object.values(gefiltert).reduce((s, arr) => s + arr.length, 0)
  const bestand = data?.docs?.length ?? null

  // 🔴 Der gescheiterte ERSTE Aufbau sperrt die Suche (N8.5) und nennt den
  // Grund ueber der Leiste; nach dem ersten Aufbau bleibt sie bedienbar (N4.5).
  const sperre = Boolean(error) && !data
  const leerGrund = sperre
    ? 'error'
    : (data && bestand === 0)
      ? 'no-data'
      : (data && totalMatches === 0)
        ? 'no-match'
        : null

  const leerzustand = leerGrund ? (
    <Box sx={{ py: SP.lg }}>
      <EmptyState
        reason={leerGrund}
        message={
          leerGrund === 'error'
            // 🔴 R16 W5: ein 403 ist die Antwort, nicht der Grund — der Satz
            //    steht allein, ohne „could not be loaded" davor.
            ? (error === DOCS_403 ? error : `Documentation could not be loaded: ${error}`)
            : leerGrund === 'no-data'
              ? 'No documentation registered yet.'
              : undefined
        }
        noun={leerGrund === 'no-match' ? 'topic' : undefined}
        query={leerGrund === 'no-match' ? q : undefined}
        onClearSearch={leerGrund === 'no-match' ? () => set('q', null) : undefined}
        onRetry={leerGrund === 'error' ? refetch : undefined}
        showDuringOutage
      />
    </Box>
  ) : null

  return (
    <Stack spacing={SP.section}>
      {/* Ein gescheitertes NACH-Laden, waehrend Karten stehen: die einzige
          Meldung, die der Leerzustand nicht abdeckt. Sie steht AUSSERHALB von
          `liste-panel` — P8 zaehlt dort genau EINEN `role="status"`. */}
      {error && !sperre && (
        <Alert severity="error" variant="outlined">
          The documentation list could not be refreshed: {error}. The cards
          below are the last successful load.
        </Alert>
      )}

      {/* N8.5: der lesbare Grund steht UEBER der Leiste — und ausserhalb von
          `liste-panel`, damit P8 dort genau einen `role="status"` zaehlt. */}
      {sperre && (
        <Alert severity="info" data-testid="erster-aufbau-gesperrt">
          Search and filters are locked until the list loads. Use Try again below.
        </Alert>
      )}

      <Panel pad="card" data-testid="liste-panel">
        <ListToolbar
          shortcut="/"
          search={{
            value: q,
            // Puffermodus (N7.7): 300 ms Ruhe oder Enter uebergeben den Begriff.
            onCommit: (v) => set('q', v),
            placeholder: 'Search topics, summaries…',
            label: 'Search docs',
            inputRole: 'searchbox',
            disabled: !data,
            // N2.9/N7.3: die Suche fuellt den Rest von Z1.
            sx: { flex: 1, minWidth: 0 },
          }}
          count={leerGrund === 'error' ? undefined : {
            noun: 'topics',
            n: data ? totalMatches : null,
            m: bestand,
            loading,
          }}
          query={{ chips, clearAll }}
        >
          {loading && !data ? (
            <Box data-testid="liste-skelett" sx={{ mt: SP.tight }}>
              <Stack spacing={SP.tight}>
                {[0, 1, 2, 3, 4, 5].map(i => <Skeleton key={i} variant="rounded" height={72} />)}
              </Stack>
            </Box>
          ) : leerGrund ? (
            leerzustand
          ) : (
            <Box data-testid="liste-karte" sx={{ mt: SP.tight }}>
              <Stack spacing={SP.section}>
                {Object.entries(gefiltert).map(([category, items]) => (
                  <Stack key={category} spacing={SP.card}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: FS.xs, color: 'text.secondary',
                        textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600,
                      }}
                    >
                      {CATEGORY_LABEL[category] || category} · {items.length}
                    </Typography>
                    <Stack spacing={SP.tight}>
                      {items.map(d => <DocCard key={d.topic} doc={d} />)}
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            </Box>
          )}
        </ListToolbar>
      </Panel>

      {/* Der Komponentenkatalog liegt NICHT in der Doku-Liste (die kommt aus
          `/api/admin/docs`), sondern ist eine eigene Route. Er steht hier, weil
          eine seltene Werkzeugflaeche keinen dauerhaften Seitenleisten-Eintrag
          bekommt (pattern-library §Owner-Grundregel) — die Doku-Uebersicht ist
          ihr Eingang. 🔴 Er steht UNTER dem Daten-Panel: ueber der Suche war er
          der Defekt aus §13.3 E5. */}
      <Panel
        component={RouterLink}
        to="/design"
        data-testid="doku-katalog"
        sx={{
          textDecoration: 'none', color: 'inherit',
          display: 'block',
          transition: 'border-color 120ms, transform 120ms',
          ...hoverOnly({ borderColor: 'primary.main', transform: 'translateY(-1px)' }),
        }}
      >
        <Stack spacing={0.5}>
          <Stack direction="row" alignItems="center" spacing={SP.tight}>
            <Typography sx={{ fontSize: FS.md, fontWeight: 600 }}>
              Komponentenkatalog
            </Typography>
            <OpenInNewIcon sx={{ fontSize: FS.sm, color: 'text.disabled', ml: 'auto' }} />
          </Stack>
          <Typography variant="body2" sx={{ fontSize: FS.sm, color: 'text.secondary' }}>
            Die Entscheidungsliste des Designsystems — jede Entscheidung mit Kanon, Zuständen
            und Quelle, in Hell und Dunkel.
          </Typography>
          <Typography variant="caption" sx={{ fontSize: FS.xs, color: 'text.disabled', fontFamily: 'monospace' }}>
            /design
          </Typography>
        </Stack>
      </Panel>
    </Stack>
  )
}


function DocCard({ doc }) {
  return (
    <Panel
      component={RouterLink}
      to={`/admin/docs/${doc.topic}`}
      pad="card"
      sx={{
        textDecoration: 'none', color: 'inherit',
        display: 'block',
        transition: 'border-color 120ms, transform 120ms',
        ...hoverOnly({ borderColor: 'primary.main', transform: 'translateY(-1px)' }),
      }}
    >
      <Stack spacing={0.5}>
        <Stack direction="row" alignItems="center" spacing={SP.tight}>
          {/* 🔴 G14a-2-Nachbesserung 1, Probe 5: der Kartentitel traegt die
              Hausmarke `karte-titel-<id>`. P12 (Suchrundreise) zaehlt die
              Treffer einer Suche ueber `SEL.zeile` UND `[data-testid^=
              "karte-titel-"]`; E5 hat keine Tabelle, und ohne die Marke zaehlte
              die Probe 0 Treffer fuer „render" — obwohl die Karte da war. Sie
              ist zugleich der Anker fuer P15/P2 auf einer Kartenliste. */}
          <Typography
            data-testid={`karte-titel-${doc.topic}`}
            sx={{ fontSize: FS.md, fontWeight: 600 }}
          >
            {doc.title}
          </Typography>
          {!doc.exists && (
            <Pill tone="warn">Missing in build</Pill>
          )}
          <OpenInNewIcon sx={{ fontSize: 12, color: 'text.disabled', ml: 'auto' }} />
        </Stack>
        <Typography variant="body2" sx={{ fontSize: FS.sm, color: 'text.secondary' }}>
          {doc.summary}
        </Typography>
        <Typography variant="caption" sx={{ fontSize: FS.xs, color: 'text.disabled', fontFamily: 'monospace' }}>
          docs/knowledge/{doc.path}
        </Typography>
      </Stack>
    </Panel>
  )
}
